import './css/certification.scss';
import axios from "axios";
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from "react-router-dom";
import { Shield, Loader2, MousePointerClick } from 'lucide-react';

const API_BASE_URL = "/httpApi/web";

export default function CertificationView() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [step, setStep] = useState('loading');
    const [verificationData, setVerificationData] = useState(null);

    const storeIdx = searchParams.get("storeIdx");
    const hash = searchParams.get("hash");

    const stepRef = useRef(step);
    useEffect(() => {
        stepRef.current = step;
    }, [step]);

    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // 주문번호 생성
    const init_orderid = () => {
        const today = new Date();
        const year = today.getFullYear();
        let month = today.getMonth() + 1;
        const date = today.getDate();
        const time = today.getTime();
        if (parseInt(month) < 10) month = "0" + month;
        const vOrderID = year + "" + month + "" + date + "" + time;
        if (document.hash_info && document.hash_info.ordr_idxx) {
            document.hash_info.ordr_idxx.value = vOrderID;
        }
    };

    // [핵심 로직] KCP로 직접 폼 전송 (v2)
    const auth_type_check = (popupWindow, call_url, reg_cert_key, ordr_idxx) => {
        const auth_form = document.getElementById("form3");

        // 1. 팝업 상태 확인
        if (!popupWindow || popupWindow.closed) {
            setStep('initial');
            alert("브라우저가 인증 팝업을 차단했습니다.\n화면의 '본인인증 시작하기' 버튼을 직접 눌러주세요.");
            return false;
        }

        // 2. 폼 데이터 설정
        auth_form.action = call_url;
        auth_form.reg_cert_key.value = reg_cert_key;
        auth_form.ordr_idxx.value = ordr_idxx; // 주문번호 추가
        auth_form.target = "auth_popup";

        // 3. 메시지 수신 및 팝업 감지 로직
        let popupTimer = null;

        const cleanUpListeners = () => {
            if (popupTimer) clearInterval(popupTimer);
            window.removeEventListener("focus", checkPopupStatus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };

        const checkPopupStatus = () => {
            if (popupWindow && popupWindow.closed) {
                cleanUpListeners();
                if (stepRef.current !== 'complete') {
                    alert("인증이 중단되었습니다. 인증이 종료됩니다.");
                    setStep('initial');
                    window.close();
                    navigate(-1);
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkPopupStatus();
            }
        };

        popupTimer = setInterval(checkPopupStatus, 1000);
        window.addEventListener("focus", checkPopupStatus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        const handleMessage = (event) => {  
            // [디버깅 1] 메시지가 오긴 왔는지 확인
            // console.log("📩 메시지 수신됨!", event);
            // console.log("📩 받은 데이터:", event.data);
            // [중요] 기존의 엄격한 도메인 체크를 잠시 주석 처리합니다.
            // 아이폰이나 특정 환경에서 도메인이 미묘하게 다를 수 있습니다 (예: www 유무, 포트번호 등)
            const allowedOrigins = [
                "http://localhost:3004", 
                "http://192.168.0.16:3004", 
                "https://admin.freepasskr.com", 
                "https://certification.freepasskr.com",
                window.location.origin
            ];
            if (!allowedOrigins.includes(event.origin)) {
                return;
            }
            if (!event.data || !event.data.auth_data) {
                return;
            }
            const authData = event.data.auth_data;

            // [추가] 인증 실패 처리
            if (authData.res_cd && authData.res_cd !== '0000') {
                cleanUpListeners();
                window.removeEventListener("message", handleMessage);
                alert("인증에 실패하였습니다: ");
                setStep('initial');
                window.close();
                navigate(-1);
            }

            // 성공 시 리스너 제거
            cleanUpListeners();
            window.removeEventListener("message", handleMessage);

            // 데이터 추출
            const year = authData.birth_day?.substring(0, 4) || "";
            const month = authData.birth_day?.substring(4, 6) || "";
            const day = authData.birth_day?.substring(6, 8) || "";

            const genderCode = authData.sex_code;
            const gender = genderCode === "01" ? "M" : "F";

            const data = {
                name: authData.user_name,
                birth: `${year}-${month}-${day}`,
                phone: authData.phone_no,
                gender: gender
            };

            setVerificationData(data);
            setStep('complete');

            postCertificationResult(storeIdx, data, hash);
        };

        window.addEventListener("message", handleMessage);

        // KCP 서버로 제출
        auth_form.submit();

        return true;
    }

    const getHash = async (_data, popupWindow) => {
        try {
            // v2 거래등록 요청 (SITE_CD 등은 서버가 알아서 처리)
            const response = await axios.post(`${API_BASE_URL}/getHash`, { data: _data });
            const { header, body } = response.data;

            if (header.success) {
                // v2에서는 call_url과 reg_cert_key, ordr_idxx를 사용
                auth_type_check(popupWindow, body.call_url, body.reg_cert_key, body.ordr_idxx);
            } else {
                if (popupWindow) popupWindow.close();
                throw new Error(header.message || "해시 생성 실패");
            }
        } catch (err) {
            if (popupWindow) popupWindow.close();
            setStep('initial');
            alert("오류 발생: " + err.message);
        }
    };

    const handleVerificationStart = () => {
        if (step === 'complete') return;

        // 1. 버튼 클릭 시 즉시 팝업 오픈 (iOS 차단 방지)
        const width = 410;
        const height = 500;
        const leftpos = window.screen.width / 2 - (width / 2);
        const toppos = window.screen.height / 2 - (height / 2);
        const winopts = `width=${width},height=${height},toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,left=${leftpos},top=${toppos}`;

        const popupWindow = window.open('about:blank', 'auth_popup', winopts);

        if (!popupWindow || popupWindow.closed) {
            setStep('initial');
            alert("팝업 차단을 해제해주세요.");
            return;
        }

        setStep('loading');

        // 2. 데이터 준비 및 해시 요청
        const tar1 = document.getElementsByName("ordr_idxx")[0];

        if (!tar1) {
            if (popupWindow) popupWindow.close();
            setStep('initial');
            return;
        }

        const jsonData = {
            ordr_idxx: tar1.value
        };

        getHash(jsonData, popupWindow);
    };

    const postCertificationResult = async (_storeIdx, _data, _hash) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/certification`, {
                storeIdx: _storeIdx,
                name: _data.name,
                birth: _data.birth,
                phone: _data.phone,
                hash: _hash,
                gender: _data.gender
            });
            const data = response.data;
            const isSuccess = (data.header && data.header.success) || data.success === true;

            if (isSuccess) {
                alert("본인인증이 완료되었습니다.");
                if (window.opener) {
                    window.close();
                    navigate(-1);
                } else {
                    window.location.href = "about:blank";
                    window.close();
                    navigate(-1);
                }
            } else {
                alert("인증 정보 전송 실패: ");
                setStep('initial');
            }
        } catch (err) {
            alert("서버 통신 중 오류 발생");
            setStep('initial');
        }
    };

    useEffect(() => {
        if (!storeIdx || !hash) {
            alert("비정상적인 접근입니다.");
            window.close();
            navigate(-1);
            return;
        }
        init_orderid();

        const timer = setTimeout(() => {
            handleVerificationStart();
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="certification-view">
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 relative">
                    {/* UI 부분은 기존과 동일하므로 유지 */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
                            <Shield className="w-8 h-8 text-indigo-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">본인인증</h1>
                        <p className="text-gray-600 text-sm">
                            안전한 서비스 이용을 위해 본인인증이 필요합니다
                        </p>
                    </div>

                    {/* Hidden Forms */}
                    <form id="form1" name="hash_info" method="post" style={{ "display": "none" }}>
                        <input type="text" name="ordr_idxx" onChange={handleChange} />
                    </form>
                    <form id="form2" name="form_hash" method="post" style={{ "display": "none" }}>
                        <input type="hidden" name="site_cd" />
                        <input type="hidden" name="ordr_idxx" />
                        <input type="hidden" name="web_siteid" />
                        <input type="hidden" name="up_hash" />
                        <input type="hidden" name="kcp_merchant_time" />
                        <input type="hidden" name="kcp_cert_lib_ver" />
                        <input type="hidden" name="web_siteid_hashYN" />
                        <input type="hidden" name="make_req_dt" />
                    </form>

                    {/* [중요] KCP로 직접 보낼 폼 (v2) 
                        - call_url은 서버에서 받아 action으로 동적 할당
                    */}
                    <form id="form3" name="form_auth" method="post" acceptCharset="EUC-KR" style={{ "display": "none" }}>
                        <input type="hidden" name="reg_cert_key" />
                        <input type="hidden" name="ordr_idxx" />
                        <input type="hidden" name="kcp_page_submit_yn" value="N" />
                    </form>

                    {/* Loading UI */}
                    {step === 'loading' && (
                        <div className="space-y-6 py-8">
                            <div className="flex flex-col items-center">
                                <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mb-4" />
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">본인인증 진행중</h2>
                                <p className="text-gray-600 text-sm text-center">
                                    인증창을 열고 있습니다...<br />
                                    잠시만 기다려주세요.
                                </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4 text-center">
                                <p className="text-xs text-blue-600 mb-2">
                                    창이 열리지 않는다면 브라우저 팝업 차단을 확인해주세요.
                                </p>
                                {/* <button 
                                    onClick={handleVerificationStart}
                                    className="text-indigo-600 text-sm font-semibold underline flex items-center justify-center gap-1 mx-auto"
                                >
                                    <MousePointerClick className="w-4 h-4" />
                                    여기를 눌러 수동으로 열기
                                </button> */}
                            </div>
                        </div>
                    )}

                    {/* Initial / Retry UI */}
                    {step === 'initial' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5">
                                        <MousePointerClick className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-amber-800">
                                            자동 실행이 차단되었습니다.
                                        </p>
                                        <p className="text-xs text-amber-600 mt-1">
                                            브라우저 보안 설정으로 인해 인증창이 자동으로 열리지 않았습니다. 아래 버튼을 눌러 진행해주세요.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleVerificationStart}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                            >
                                <Shield className="w-5 h-5" />
                                본인인증 시작하기
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}